import { atom } from "jotai"
import { atomWithStorage } from "jotai/utils"
import { safeJSONSessionStorage } from "@/utils/safe-jotai-storage"
import { decodeStyle } from "@/lib/catalog/style-codec"
import { StyleRecord } from "@/storage/indexeddb"
import { StyleResponseDTO, AIStyleCategoryDTO, StyleVisibility } from "@/api/dto/styles"
import { atomWithQuery } from "jotai-tanstack-query"
import { PNApiOperations } from "@/api/operations"
import { ListingResponseDTO } from "@/api/dto/shared"
import { showInternalStylesAtom } from "@/store/settings-state"

export interface PolarrStyle {
	title: string
	styleUrl: string
	shortDescription: string
	description: string
	coverPhoto: string
	samplePhotos: string[]
	beforeAfterPhotos: string[][]
	styleRecord?: StyleRecord
	category: string
	isFeatured?: boolean
}

// Fetch categories from backend
const fetchStyleCategoriesAtom = atomWithQuery<ListingResponseDTO<AIStyleCategoryDTO>>((get) => {
	const showInternalStyles = get(showInternalStylesAtom)
	const visibility: StyleVisibility | StyleVisibility[] = showInternalStyles
		? ["public", "internal"]
		: "public"

	return {
		queryKey: ["style_categories", visibility],
		queryFn: () => PNApiOperations.getStyleCategories(visibility),
	}
})

export const styleCategoriesAtom = atom<{
	data: AIStyleCategoryDTO[]
	isInitialLoading: boolean
}>((get) => {
	const { data, isInitialLoading, isError } = get(fetchStyleCategoriesAtom)

	if (data && !isError) {
		// Convert createdAt and updatedAt strings to Date objects
		for (const category of data.data) {
			if (typeof category.createdAt === "string") {
				category.createdAt = new Date(category.createdAt)
			}
			if (typeof category.updatedAt === "string") {
				category.updatedAt = new Date(category.updatedAt)
			}
		}
		const filteredData = data.data.filter(
			(category) => category.name.trim().length > 0 && category.styleCount > 0,
		)
		// Sort by orderScore
		const sortedData = [...filteredData].sort((a, b) => a.orderScore - b.orderScore)
		return { data: sortedData, isInitialLoading }
	}

	return {
		data: [],
		isInitialLoading,
	}
})

// Helper to get category for dropdown/tabs
export const categoryNamesAtom = atom<string[]>((get) => {
	const categories = get(styleCategoriesAtom)
	return categories.data.map((cat) => cat.name)
})

let publicStylesByCategoryPromise: Promise<Record<string, StyleResponseDTO[]>> | null = null

export const fetchAllPublicStylesByCategory = async (): Promise<
	Record<string, StyleResponseDTO[]>
> => {
	if (!publicStylesByCategoryPromise) {
		publicStylesByCategoryPromise = (async () => {
			const categoriesResponse = await PNApiOperations.getStyleCategories("public")
			const categories = categoriesResponse.data
				.filter((category) => category.name.trim().length > 0 && category.styleCount > 0)
				.sort((a, b) => a.orderScore - b.orderScore)

			const stylesByCategory = await Promise.all(
				categories.map((category) =>
					PNApiOperations.getCategoryStyles(category.categoryId),
				),
			)

			const normalizedStylesByCategory = stylesByCategory.map((listing) =>
				listing.data.map((style) => {
					if (typeof style.createdAt === "string") {
						style.createdAt = new Date(style.createdAt)
					}
					if (typeof style.updatedAt === "string") {
						style.updatedAt = new Date(style.updatedAt)
					}
					return style
				}),
			)

			return Object.fromEntries(
				categories.map((category, index) => [
					category.name,
					normalizedStylesByCategory[index] ?? [],
				]),
			)
		})()
	}

	try {
		return await publicStylesByCategoryPromise
	} catch (error) {
		publicStylesByCategoryPromise = null
		throw error
	}
}

// Public styles helper: load all public styles grouped by category name
const fetchAllPublicStylesByCategoryAtom = atomWithQuery<Record<string, StyleResponseDTO[]>>(
	() => ({
		queryKey: ["polarr_styles_by_category", "public"],
		queryFn: () => fetchAllPublicStylesByCategory(),
	}),
)

export const allPublicStylesByCategoryAtom = atom<{
	data: Record<string, StyleResponseDTO[]>
	isInitialLoading: boolean
}>((get) => {
	const { data, isInitialLoading, isError } = get(fetchAllPublicStylesByCategoryAtom)
	if (data && !isError) {
		return { data, isInitialLoading }
	}
	return { data: {}, isInitialLoading }
})

export const fetchPolarrStyle = async (url: string) => {
	try {
		const data = await fetch(url).then((res) => res.arrayBuffer())

		const styleRecord = decodeStyle(data)
		return styleRecord
	} catch (e) {
		console.error(e)
		return null
	}
}

// Selected category management
const rawSelectedCategoryAtom = atomWithStorage<string | null>(
	"ai_styles_selected_category",
	null,
	safeJSONSessionStorage<string | null>(),
)

export const selectedCategoryAtom = atom(
	(get) => {
		const categories = get(styleCategoriesAtom)
		const storedValue = get(rawSelectedCategoryAtom)

		// If we have categories loaded
		if (categories.data.length > 0) {
			// Check if stored value is valid
			const isValid = categories.data.some((cat) => cat.name === storedValue)
			if (isValid && storedValue) {
				return storedValue
			}
			// Default to first category
			return categories.data[0]?.name ?? "All"
		}

		// Fallback while loading
		return storedValue ?? "All"
	},
	(_get, set, next: string) => {
		set(rawSelectedCategoryAtom, next)
	},
)

// Get the selected category ID (for API calls)
export const selectedCategoryIdAtom = atom<string | null>((get) => {
	const categories = get(styleCategoriesAtom)
	const selectedName = get(selectedCategoryAtom)
	return categories.data.find((cat) => cat.name === selectedName)?.categoryId ?? null
})

export type NavigationStyle = StyleResponseDTO
export const currentDisplayedStylesAtom = atom<NavigationStyle[]>([])
export const currentStyleIndexAtom = atom<number>(-1)
